import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { MessageCircle, Plus, Search, Trash2, Send, CheckCircle2, XCircle, Clock, Phone, Users, TrendingUp, X, Loader2 } from 'lucide-react';

interface WhatsAppMessage {
  id: number;
  phoneNumber: string;
  message: string;
  templateName?: string;
  status: string;
  errorMessage?: string;
  sentAt?: string;
  deliveredAt?: string;
  recipientName?: string;
  shipmentId?: number;
  orderId?: number;
  createdBy?: string;
  createdAt: string;
}

const T = {
  title: "واتس أب",
  subtitle: "إرسال وإدارة رسائل واتس أب",
  sendNew: "إرسال رسالة جديدة",
  sendBulk: "إرسال جماعي",
  totalMessages: "إجمالي الرسائل",
  sentMessages: "الرسائل المرسلة",
  deliveredMessages: "الرسائل المسلمة",
  failedMessages: "الرسائل الفاشلة",
  todayMessages: "رسائل اليوم",
  pendingMessages: "قيد الإرسال",
  search: "بحث بالرقم أو الاسم...",
  loading: "جاري التحميل...",
  noMessages: "لا توجد رسائل",
  thRecipient: "المستلم",
  thPhone: "رقم الهاتف",
  thMessage: "الرسالة",
  thStatus: "الحالة",
  thDate: "التاريخ",
  thActions: "الإجراءات",
  delete: "حذف",
  deleteAll: "حذف الكل",
  confirmDelete: "هل أنت متأكد من حذف هذه الرسالة؟",
  confirmDeleteAll: "هل أنت متأكد من حذف جميع الرسائل؟",
  deleted: "تم حذف الرسالة بنجاح",
  allDeleted: "تم حذف جميع الرسائل بنجاح",
  sent: "تم الإرسال",
  delivered: "تم التسليم",
  failed: "فاشل",
  pending: "قيد الإرسال",
  sending: "جاري الإرسال",
  allStatus: "جميع الحالات",
  error: "حدث خطأ",
  sendSuccess: "تم إرسال الرسالة بنجاح",
  sendFailed: "فشل إرسال الرسالة",
  lblPhone: "رقم الهاتف",
  lblName: "اسم المستلم",
  lblMessage: "الرسالة",
  lblBulkPhones: "أرقام الهواتف (رقم في كل سطر)",
  cancel: "إلغاء",
  send: "إرسال",
  close: "إغلاق",
  placeholderPhone: "+20 100 000 0000",
  placeholderMessage: "اكتب الرسالة هنا..."
};

export default function WhatsAppPage() {
  const [messages, setMessages] = useState<WhatsAppMessage[]>([]);
  const [stats, setStats] = useState({ total: 0, sent: 0, delivered: 0, failed: 0, pending: 0, todayCount: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showSendModal, setShowSendModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ phoneNumber: '', recipientName: '', message: '' });
  const [bulkForm, setBulkForm] = useState({ phones: '', message: '' });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [msgRes, statsRes] = await Promise.all([
        api.get('/api/whatsapp?pageSize=100'),
        api.get('/api/whatsapp/stats')
      ]);
      if (msgRes.data?.success) {
        const data = msgRes.data.data;
        setMessages(Array.isArray(data) ? data : (data.items || []));
      }
      if (statsRes.data?.success) setStats(statsRes.data.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleSend = async () => {
    if (!form.phoneNumber || !form.message) return;
    setSending(true);
    try {
      const res = await api.post('/api/whatsapp/send', {
        phoneNumber: form.phoneNumber,
        message: form.message,
        recipientName: form.recipientName
      });
      if (res.data?.success) {
        alert(T.sendSuccess);
        setShowSendModal(false);
        setForm({ phoneNumber: '', recipientName: '', message: '' });
        loadData();
      } else {
        alert(T.sendFailed);
      }
    } catch (e) { alert(T.error); }
    finally { setSending(false); }
  };

  const handleBulkSend = async () => {
    const phones = bulkForm.phones.split('\n').map(p => p.trim()).filter(p => p);
    if (phones.length === 0 || !bulkForm.message) return;
    setSending(true);
    try {
      const res = await api.post('/api/whatsapp/send-bulk', {
        messages: phones.map(p => ({ phoneNumber: p, message: bulkForm.message })),
        createdBy: 'admin'
      });
      if (res.data?.success) {
        alert(res.data.message);
        setShowBulkModal(false);
        setBulkForm({ phones: '', message: '' });
        loadData();
      }
    } catch (e) { alert(T.error); }
    finally { setSending(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(T.confirmDelete)) return;
    try {
      await api.delete('/api/whatsapp/' + id);
      loadData();
      alert(T.deleted);
    } catch (e) { alert(T.error); }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(T.confirmDeleteAll)) return;
    try {
      await api.delete('/api/whatsapp/all');
      loadData();
      alert(T.allDeleted);
    } catch (e) { alert(T.error); }
  };

  const filtered = messages.filter(m => {
    const matchSearch = !search ||
      m.phoneNumber.includes(search) ||
      (m.recipientName || '').toLowerCase().includes(search.toLowerCase()) ||
      m.message.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || m.status.toLowerCase() === statusFilter.toLowerCase();
    return matchSearch && matchStatus;
  });

  const getStatusInfo = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'sent': return { label: T.sent, color: 'bg-blue-50 text-blue-700 border-blue-200', icon: CheckCircle2 };
      case 'delivered': return { label: T.delivered, color: 'bg-green-50 text-green-700 border-green-200', icon: CheckCircle2 };
      case 'failed': return { label: T.failed, color: 'bg-red-50 text-red-700 border-red-200', icon: XCircle };
      case 'pending': case 'sending': return { label: T.pending, color: 'bg-yellow-50 text-yellow-700 border-yellow-200', icon: Clock };
      default: return { label: status, color: 'bg-gray-50 text-gray-700 border-gray-200', icon: Clock };
    }
  };

  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500 transition';

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{T.title}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{T.subtitle}</p>
          </div>
        </h1>
        <div className="flex gap-2">
          <Button onClick={() => setShowBulkModal(true)} variant="outline" className="border-emerald-500 text-emerald-700 hover:bg-emerald-50">
            <Users className="w-4 h-4 ml-2" />
            {T.sendBulk}
          </Button>
          <Button onClick={() => setShowSendModal(true)} className="bg-gradient-to-l from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white">
            <Plus className="w-5 h-5 ml-2" />
            {T.sendNew}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center"><MessageCircle className="w-5 h-5 text-emerald-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalMessages}</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-green-600" /></div></div>
          <p className="text-sm text-gray-500">{T.deliveredMessages}</p>
          <p className="text-2xl font-bold text-green-700">{stats.delivered}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><Send className="w-5 h-5 text-blue-600" /></div></div>
          <p className="text-sm text-gray-500">{T.sentMessages}</p>
          <p className="text-2xl font-bold text-blue-700">{stats.sent}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center"><XCircle className="w-5 h-5 text-red-600" /></div></div>
          <p className="text-sm text-gray-500">{T.failedMessages}</p>
          <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><TrendingUp className="w-5 h-5 text-purple-600" /></div></div>
          <p className="text-sm text-gray-500">{T.todayMessages}</p>
          <p className="text-2xl font-bold text-purple-700">{stats.todayCount}</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" placeholder={T.search} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500 transition" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500">
              <option value="all">{T.allStatus}</option>
              <option value="sent">{T.sent}</option>
              <option value="delivered">{T.delivered}</option>
              <option value="failed">{T.failed}</option>
              <option value="pending">{T.pending}</option>
            </select>
            <Button onClick={handleDeleteAll} variant="outline" className="border-red-300 text-red-700 hover:bg-red-50">
              <Trash2 className="w-4 h-4 ml-2" />
              {T.deleteAll}
            </Button>
          </div>

          {loading ? (
            <div className="p-12 text-center"><Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto" /><p className="text-gray-500 mt-4">{T.loading}</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">{T.noMessages}</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thRecipient}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thPhone}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thMessage}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thStatus}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thDate}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thActions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((msg) => {
                    const statusInfo = getStatusInfo(msg.status);
                    const StatusIcon = statusInfo.icon;
                    return (
                      <tr key={msg.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                        <td className="py-4 px-5">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white">
                              <Phone className="w-4 h-4" />
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{msg.recipientName || '-'}</p>
                              {msg.createdBy && <p className="text-xs text-gray-500">{msg.createdBy}</p>}
                            </div>
                          </div>
                        </td>
                        <td className="py-4 px-5"><span className="font-mono text-sm text-gray-700">{msg.phoneNumber}</span></td>
                        <td className="py-4 px-5"><p className="text-sm text-gray-700 max-w-md truncate">{msg.message}</p></td>
                        <td className="py-4 px-5">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${statusInfo.color}`}>
                            <StatusIcon className="w-3 h-3" />
                            {statusInfo.label}
                          </span>
                          {msg.errorMessage && <p className="text-xs text-red-500 mt-1">{msg.errorMessage}</p>}
                        </td>
                        <td className="py-4 px-5">
                          <p className="text-sm text-gray-700">{new Date(msg.createdAt).toLocaleString('ar-EG')}</p>
                        </td>
                        <td className="py-4 px-5">
                          <button onClick={() => handleDelete(msg.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title={T.delete}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showSendModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-lg"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{T.sendNew}</h2>
              <button onClick={() => setShowSendModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div><Label>{T.lblPhone} *</Label><Input value={form.phoneNumber} onChange={(e: any) => setForm({...form, phoneNumber: e.target.value})} className={inputClass} placeholder={T.placeholderPhone} /></div>
              <div><Label>{T.lblName}</Label><Input value={form.recipientName} onChange={(e: any) => setForm({...form, recipientName: e.target.value})} className={inputClass} /></div>
              <div><Label>{T.lblMessage} *</Label><textarea value={form.message} onChange={(e: any) => setForm({...form, message: e.target.value})} className={inputClass} rows={5} placeholder={T.placeholderMessage} /></div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowSendModal(false)} className="flex-1">{T.cancel}</Button>
              <Button onClick={handleSend} disabled={sending} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {sending ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <Send className="w-4 h-4 ml-2" />}
                {T.send}
              </Button>
            </div>
          </div></Card>
        </div>
      )}

      {showBulkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-2xl"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{T.sendBulk}</h2>
              <button onClick={() => setShowBulkModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div><Label>{T.lblBulkPhones} *</Label><textarea value={bulkForm.phones} onChange={(e: any) => setBulkForm({...bulkForm, phones: e.target.value})} className={inputClass + ' font-mono text-sm'} rows={6} placeholder={"+201000000001\n+201000000002\n+201000000003"} /></div>
              <div><Label>{T.lblMessage} *</Label><textarea value={bulkForm.message} onChange={(e: any) => setBulkForm({...bulkForm, message: e.target.value})} className={inputClass} rows={4} placeholder={T.placeholderMessage} /></div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">{"سيتم إرسال "}{bulkForm.phones.split('\n').filter(p => p.trim()).length}{" رسالة"}</p>
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowBulkModal(false)} className="flex-1">{T.cancel}</Button>
              <Button onClick={handleBulkSend} disabled={sending} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                {sending ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <Send className="w-4 h-4 ml-2" />}
                {T.send}
              </Button>
            </div>
          </div></Card>
        </div>
      )}
    </div>
  );
}
